import { AuthFetch, PagedList, PrmService } from '../gs-core/common'; import {
  DEFAULT_PAGE_SIZE,
  Doc,
  DocModel,
  FilterParams, FilterParamsModel, hydrateFull, MAX_PAGE_SIZE
} from '../common/models';
import { ValidationError } from '../gs-core/common/models';

interface SessionStorage {
  getItem: (key: string) => string;
  setItem: (key: string, value: string) => void;
}

export class DocService {
  private FILTERS_KEY = 'DocService.filtersUsedToBuildCache.sessionStorageKey';
  private IDS_KEY = 'DocService.cachedIds.sessionStorageKey';

  maxCachedIds = MAX_PAGE_SIZE;
  filtersUsedToBuildCache: FilterParams | undefined;
  cachedIds: PagedList<number> | undefined;

  constructor(private authFetch: AuthFetch, private prmService: PrmService, private sessionStorage: SessionStorage) { }

  async findIds(filters: FilterParams): Promise<number[]> {
    const { page = 1, pageSize = DEFAULT_PAGE_SIZE } = filters;
    const offset = (page - 1) * pageSize;

    this.loadFromSessionStorage();

    if (!this.cachedIds || this.shouldRebuildCache(filters)) {
      const idsFilters = FilterParamsModel.create({ ...filters, page: 1, pageSize: this.maxCachedIds });
      const url = idsFilters.toDocListUrl(true);
      const cachedIds = await this.authFetch(url) as PagedList<number>;
      this.setCache(filters, cachedIds);
    }

    if (this.cachedIds && this.cachedIds.records.length && !this.pageAlreadyInCache(page, pageSize)) {
      throw new ValidationError('Please filter search results to see more documents.');
    }

    return (this.cachedIds as PagedList<number>).records.slice(offset, offset + pageSize);
  }

  loadFromSessionStorage() {
    if (!this.cachedIds || !this.filtersUsedToBuildCache) {
      const cachedFiltersStr = this.sessionStorage.getItem(this.FILTERS_KEY);
      const cachedIdsStr = this.sessionStorage.getItem(this.IDS_KEY);
      if (cachedFiltersStr && cachedIdsStr) {
        this.filtersUsedToBuildCache = FilterParamsModel.create(JSON.parse(cachedFiltersStr));
        this.cachedIds = JSON.parse(cachedIdsStr);
      }
    }
  }

  setCache(filters: FilterParams, cachedIds: PagedList<number>) {
    this.filtersUsedToBuildCache = filters;
    this.cachedIds = cachedIds;
    this.sessionStorage.setItem(this.FILTERS_KEY, JSON.stringify(filters));
    this.sessionStorage.setItem(this.IDS_KEY, JSON.stringify(cachedIds));
  }

  shouldRebuildCache(filters: FilterParams) {
    return !this.filtersUsedToBuildCache || this.filtersUsedToBuildCache.expectResultStructureToChange(filters);
  }

  pageAlreadyInCache(page: number, pageSize: number) {
    if (this.cachedIds) {
      const startOffset = (page - 1) * pageSize;
      const endOffset = Math.min(startOffset + pageSize - 1, this.cachedIds.total - 1);
      const firstCachedOffset = (this.cachedIds.page - 1) * pageSize;
      const lastCachedOffset = firstCachedOffset + this.cachedIds.records.length - 1;
      return (
        startOffset >= firstCachedOffset &&
        startOffset <= lastCachedOffset &&
        endOffset >= firstCachedOffset &&
        endOffset <= lastCachedOffset
      );
    }
    return false;
  }

  docIdToPage(docId: number): number | undefined {
    return this.cachedIds && (this.cachedIds.records.indexOf(docId) + 1);
  }

  pageToDocId(page: number): number | undefined {
    const pageSize = 1;
    if (this.cachedIds && this.pageAlreadyInCache(page, pageSize)) {
      const offset = (page - 1) * pageSize;
      return this.cachedIds.records[offset];
    }
    return undefined;
  }

  overrideCachedIds(ids: number[]) {
    const filters = FilterParamsModel.create({});
    const cachedIds = { page: 1, total: ids.length, records: ids };
    this.setCache(filters, cachedIds);
  }

  async findByIds(ids: number[]): Promise<Doc[]> {
    const filters = FilterParamsModel.create({ ids });
    const url = `/api/doc?${filters.toQueryString()}`;
    const docsJson = await this.authFetch(url) as Doc[];
    return docsJson.map(doc => DocModel.create(doc));
  }

  async findPage(filters: FilterParams): Promise<PagedList<Doc>> {
    const ids = await this.findIds(filters);
    if (!ids.length) {
      return {
        page: 0,
        total: 0,
        records: []
      };
    }
    const docs = await this.findByIds(ids);
    const { page = 1 } = filters;
    const { total } = this.cachedIds as PagedList<number>;
    return { page, total, records: docs };
  }

  async findOne(id: number): Promise<Doc> {
    this.loadFromSessionStorage();

    const doc = await this.authFetch(`/api/doc/${id}`) as Doc;
    return hydrateFull(doc, this.prmService);
  }

  async save(docToSave: Doc): Promise<Doc> {
    const savedDoc = await this.authFetch(
      `/api/doc/${docToSave.id}`,
      {
        method: 'PUT',
        body: { doc: docToSave }
      }
    ) as Doc;

    // If the savedDoc had a url/thumbnail put them back on since api save doesn't recreate them
    const url = savedDoc.url || docToSave.url;
    const thumbnail = savedDoc.thumbnail || docToSave.thumbnail;
    return hydrateFull({ ...savedDoc, url, thumbnail }, this.prmService);
  }

  async delete(docToDelete: Doc): Promise<Doc> {
    const deletedDocDoc = await this.authFetch(`/api/doc/${docToDelete.id}`, { method: 'DELETE' }) as Doc;
    const url = deletedDocDoc.url || docToDelete.url;
    const thumbnail = deletedDocDoc.thumbnail || docToDelete.thumbnail;
    return hydrateFull({ ...deletedDocDoc, url, thumbnail }, this.prmService);
  }

  async restore(docToRestore: Doc): Promise<Doc> {
    const restoredDocDoc = await this.authFetch(`/api/doc/${docToRestore.id}/restore`, { method: 'PUT' }) as Doc;
    const url = restoredDocDoc.url || docToRestore.url;
    const thumbnail = restoredDocDoc.thumbnail || docToRestore.thumbnail;
    return hydrateFull({ ...restoredDocDoc, url, thumbnail }, this.prmService);
  }

  async create(file: File): Promise<Doc> {
    const filename = encodeURIComponent(file.name);

    const docJson = await this.authFetch(
      '/api/doc',
      {
        method: 'POST',
        body: { filename }
      }
    ) as Doc;

    return DocModel.create(docJson);
  }
}



// WEBPACK FOOTER //
// ./src/services/DocService.ts